import { RenderParameters } from './rouletteRenderer';
import { UIObject } from './UIObject';
import { Marble } from './marble';

export class TopCandidatesRenderer implements UIObject {
  private isGameStarted = false;
  private topCandidates: { marble: Marble; rank: number; isWinner?: boolean }[] = [];

  constructor() {}

  setGameStarted(started: boolean) {
    this.isGameStarted = started;
  }

  render(
    ctx: CanvasRenderingContext2D,
    { winners, marbles, winnerRank, stage, totalMarbleCount, winnerCount }: RenderParameters,
    width: number,
    height: number,
  ) {
    // 게임이 시작되지 않았으면 표시하지 않음
    if (!this.isGameStarted) {
      return;
    }

    // 구슬도 없고 골인한 구슬도 없으면 표시하지 않음
    if (marbles.length < 1 && winners.length < 1) {
      return;
    }

    // 마지막 당첨 모드인지 확인
    const isLastWinner = winnerRank >= totalMarbleCount - 1;

    // 표시할 후보 수 결정 (1~5명이면 5명, 6~20명이면 winnerCount만큼)
    const displayCount = winnerCount <= 5 ? 5 : winnerCount;

    // 현재 유력한 후보 계산
    this.calculateTopCandidates(marbles, winners, winnerRank, isLastWinner, displayCount);

    if (this.topCandidates.length === 0) {
      return;
    }

    ctx.save();

    // 박스 높이는 표시할 후보 수에 따라 동적 계산
    const boxWidth = 150;
    const padding = 12;
    const lineHeight = 26;
    const titleHeight = 30;
    const boxHeight = titleHeight + lineHeight * this.topCandidates.length + padding;
    const startX = width - boxWidth - 90;
    const startY = height - boxHeight - 30;

    // 반투명 배경 with glow 효과
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.strokeStyle = '#00FFA3';
    ctx.lineWidth = 2;
    ctx.shadowBlur = 25;
    ctx.shadowColor = '#00FFA3';
    ctx.beginPath();
    ctx.roundRect(startX, startY, boxWidth, boxHeight, 10);
    ctx.fill();
    ctx.stroke();
    
    ctx.shadowBlur = 0;

    // 최종 우승자 확정 여부 (winnerCount가 2명 이상일 때만 적용)
    // - 마지막 당첨 모드: 남은 구슬이 winnerCount 이하
    // - 첫번째 당첨 모드: 골인한 구슬이 winnerCount 이상
    const isFinalWinnersForLast = isLastWinner && marbles.length <= winnerCount && marbles.length > 0;
    const isFinalWinnersForFirst = !isLastWinner && winners.length >= winnerCount;
    const isFinalWinners = winnerCount >= 2 && (isFinalWinnersForLast || isFinalWinnersForFirst);

    // 타이틀
    ctx.fillStyle = '#00FFA3';
    ctx.font = 'bold 10pt NanumSquareNeo, sans-serif';
    ctx.textAlign = 'left';
    const titleText = isFinalWinners ? `🎉 최종 우승 ${winnerCount}인` : '🏆 유력 우승 후보';
    ctx.fillText(titleText, startX + padding + 1, startY + padding + 14);

    // 후보자 표시
    ctx.font = 'bold 9pt NanumSquareNeo, sans-serif';
    this.topCandidates.forEach((candidate, index) => {
      const y = startY + padding + 14 + lineHeight * (index + 1);
      
      const badgeX = startX + padding;
      const badgeY = y - 8;
      const badgeSize = 20;
      
      const badgeColor = '#00FFA3';
      
      ctx.fillStyle = badgeColor;
      ctx.beginPath();
      ctx.arc(badgeX + badgeSize / 2, badgeY + badgeSize / 2, badgeSize / 2, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#000';
      ctx.font = 'bold 9 NanumSquareNeo, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`${index + 1}`, badgeX + badgeSize / 2, badgeY + badgeSize / 2 + 4);
      
      ctx.fillStyle = candidate.marble.color;
      ctx.font = 'bold 9pt NanumSquareNeo, sans-serif';
      ctx.textAlign = 'left';
      
      let displayText: string;
      if (winnerCount >= 2 && isFinalWinners) {
        // 2명 이상 우승 모드이고 최종 우승자 확정 시: 모두 "우승!" 표시
        displayText = `${candidate.marble.name} 🎉우승!`;
      } else if (candidate.isWinner) {
        displayText = `${candidate.marble.name} (골인!)`;
      } else {
        const heightValue = Math.max(0, Math.floor(stage.goalY - candidate.marble.y));
        displayText = `${candidate.marble.name} (${heightValue}m)`;
      }
      
      ctx.fillText(
        displayText,
        badgeX + badgeSize + 5,
        y + 4
      );
    });

    ctx.restore();
  }

  private calculateTopCandidates(
    marbles: Marble[],
    winners: Marble[],
    winnerRank: number,
    isLastWinner: boolean,
    displayCount: number,
  ) {
    const sortedMarbles = isLastWinner 
      ? [...marbles].sort((a, b) => a.y - b.y)
      : [...marbles].sort((a, b) => b.y - a.y);
    
    const recentWinners = isLastWinner
      ? [...winners].reverse()
      : [...winners];
    
    const combinedCandidates: { marble: Marble; rank: number; isWinner: boolean }[] = [];
    
    if (isLastWinner) {
      sortedMarbles.forEach((marble, index) => {
        if (combinedCandidates.length < displayCount) {
          combinedCandidates.push({
            marble,
            rank: winners.length + index,
            isWinner: false,
          });
        }
      });
      
      recentWinners.forEach((marble, index) => {
        if (combinedCandidates.length < displayCount) {
          combinedCandidates.push({
            marble,
            rank: winners.length - 1 - index,
            isWinner: true,
          });
        }
      });
    } else {
      recentWinners.slice(0, displayCount).forEach((marble, index) => {
        combinedCandidates.push({
          marble,
          rank: index,
          isWinner: true,
        });
      });
      
      sortedMarbles.forEach((marble, index) => {
        if (combinedCandidates.length < displayCount) {
          combinedCandidates.push({
            marble,
            rank: winners.length + index,
            isWinner: false,
          });
        }
      });
    }
    
    this.topCandidates = combinedCandidates;
  }

  update(deltaTime: number) {
    // 필요시 업데이트 로직 추가
  }

  getBoundingBox() {
    return null; // 이 UI는 클릭 영역이 필요없음
  }

  onMouseDown() {
    return false;
  }

  onMouseMove() {
    return false;
  }

  onMouseUp() {
    return false;
  }

  onWheel() {}
}

